/**
 * useSessionFlow
 *
 * Manages the live meeting flow: block navigation, per-block timer, progress.
 * Synced to Firestore so all participants see the same block and timer.
 * Host writes; attendees read via onSnapshot.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import type { SessionBlock, SessionFlowState } from './types';

const COLLECTION = 'meetingSessionFlow';

interface UseSessionFlowResult {
  flow: SessionFlowState;
  currentBlock: SessionBlock | null;
  elapsedSeconds: number;
  remainingSeconds: number;
  progressPercent: number;
  totalBlocks: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  isLastBlock: boolean;
  start: () => void;
  pause: () => void;
  nextBlock: () => void;
  prevBlock: () => void;
  reset: () => void;
  resetCurrentBlock: () => void;
}

export function useSessionFlow(
  entityId: string,
  meetingId: string,
  blocks: SessionBlock[],
  options?: { autoAdvance?: boolean; canControl?: boolean },
): UseSessionFlowResult {
  const autoAdvance = options?.autoAdvance ?? false;
  /** Alleen organisator/host mag timer starten/pauzeren en blokken wisselen (Firestore writes). */
  const canControl = options?.canControl ?? true;
  const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order);
  const docId = `${entityId}_${meetingId}`;

  const [flow, setFlow] = useState<SessionFlowState>({
    entityId,
    meetingId,
    currentBlockIndex: 0,
    blockStartedAt: null,
    elapsedSeconds: 0,
    isRunning: false,
  });

  // Remote state from Firestore (source of truth)
  const [remoteBlockIndex, setRemoteBlockIndex] = useState(0);
  const [remoteIsRunning, setRemoteIsRunning] = useState(false);
  const [remoteStartedAtMs, setRemoteStartedAtMs] = useState<number | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isHostAction = useRef(false);

  const currentBlock = sortedBlocks[flow.currentBlockIndex] ?? null;
  const blockDurationSecs = (currentBlock?.durationMinutes ?? 0) * 60;
  const remainingSeconds = Math.max(0, blockDurationSecs - flow.elapsedSeconds);
  const progressPercent =
    blockDurationSecs > 0
      ? Math.min(100, Math.round((flow.elapsedSeconds / blockDurationSecs) * 100))
      : 0;

  // ── Firestore sync: write helper ────────────────────────────────────────
  const writeState = useCallback(async (data: Record<string, any>) => {
    isHostAction.current = true;
    try {
      await setDoc(
        doc(db, COLLECTION, docId),
        { ...data, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      console.error('[useSessionFlow] Firestore write error:', err);
    }
  }, [docId]);

  // ── Firestore sync: read (all participants) ─────────────────────────────
  useEffect(() => {
    if (!entityId || !meetingId) return;
    const unsub = onSnapshot(doc(db, COLLECTION, docId), (snap) => {
      if (!snap.exists()) return;
      const data: any = snap.data();

      const idx = typeof data?.currentBlockIndex === 'number' ? data.currentBlockIndex : 0;
      const running = !!data?.isRunning;
      const startedMs: number | null = data?.blockStartedAtMs ?? null;

      setRemoteBlockIndex(idx);
      setRemoteIsRunning(running);
      setRemoteStartedAtMs(startedMs);

      // If this wasn't triggered by our own action, sync local state
      if (!isHostAction.current) {
        if (running && startedMs) {
          const elapsed = Math.floor((Date.now() - startedMs) / 1000);
          setFlow((prev) => ({
            ...prev,
            currentBlockIndex: idx,
            isRunning: true,
            blockStartedAt: new Date(startedMs),
            elapsedSeconds: Math.max(0, elapsed),
          }));
        } else {
          setFlow((prev) => ({
            ...prev,
            currentBlockIndex: idx,
            isRunning: false,
            elapsedSeconds: 0,
            blockStartedAt: null,
          }));
        }
      }
      isHostAction.current = false;
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, meetingId, docId]);

  // ── Timer tick ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (flow.isRunning) {
      intervalRef.current = setInterval(() => {
        setFlow((prev) => ({ ...prev, elapsedSeconds: prev.elapsedSeconds + 1 }));
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [flow.isRunning]);

  // ── Auto-advance to next block when time runs out ─────────────────────
  const autoAdvancedRef = useRef<number>(-1);
  useEffect(() => {
    if (
      !canControl ||
      !autoAdvance ||
      !flow.isRunning ||
      blockDurationSecs <= 0 ||
      flow.currentBlockIndex >= sortedBlocks.length - 1
    ) return;

    if (flow.elapsedSeconds >= blockDurationSecs && autoAdvancedRef.current !== flow.currentBlockIndex) {
      autoAdvancedRef.current = flow.currentBlockIndex;
      const newIdx = flow.currentBlockIndex + 1;
      const nowMs = Date.now();
      setFlow((prev) => ({
        ...prev,
        currentBlockIndex: newIdx,
        elapsedSeconds: 0,
        blockStartedAt: new Date(nowMs),
      }));
      writeState({
        currentBlockIndex: newIdx,
        isRunning: true,
        blockStartedAtMs: nowMs,
      });
    }
  }, [canControl, autoAdvance, flow.isRunning, flow.elapsedSeconds, flow.currentBlockIndex, blockDurationSecs, sortedBlocks.length, writeState]);

  // Reset flow when entity or meeting changes
  useEffect(() => {
    setFlow({
      entityId,
      meetingId,
      currentBlockIndex: 0,
      blockStartedAt: null,
      elapsedSeconds: 0,
      isRunning: false,
    });
  }, [entityId, meetingId]);

  // ── Host actions (write to Firestore → all clients react) ──────────────

  const start = useCallback(() => {
    if (!canControl) return;
    const now = Date.now();
    setFlow((prev) => ({
      ...prev,
      isRunning: true,
      blockStartedAt: prev.blockStartedAt ?? new Date(now),
      elapsedSeconds: prev.elapsedSeconds, // keep existing elapsed if resuming
    }));
    writeState({
      currentBlockIndex: flow.currentBlockIndex,
      isRunning: true,
      blockStartedAtMs: now - (flow.elapsedSeconds * 1000),
    });
  }, [canControl, flow.currentBlockIndex, flow.elapsedSeconds, writeState]);

  const pause = useCallback(() => {
    if (!canControl) return;
    setFlow((prev) => ({ ...prev, isRunning: false }));
    writeState({
      isRunning: false,
    });
  }, [canControl, writeState]);

  const nextBlock = useCallback(() => {
    if (!canControl) return;
    setFlow((prev) => {
      if (prev.currentBlockIndex >= sortedBlocks.length - 1) return prev;
      const newIdx = prev.currentBlockIndex + 1;
      const nowMs = prev.isRunning ? Date.now() : null;
      writeState({
        currentBlockIndex: newIdx,
        isRunning: prev.isRunning,
        blockStartedAtMs: nowMs,
      });
      return {
        ...prev,
        currentBlockIndex: newIdx,
        elapsedSeconds: 0,
        blockStartedAt: prev.isRunning ? new Date() : null,
      };
    });
  }, [canControl, sortedBlocks.length, writeState]);

  const prevBlock = useCallback(() => {
    if (!canControl) return;
    setFlow((prev) => {
      if (prev.currentBlockIndex <= 0) return prev;
      const newIdx = prev.currentBlockIndex - 1;
      const nowMs = prev.isRunning ? Date.now() : null;
      writeState({
        currentBlockIndex: newIdx,
        isRunning: prev.isRunning,
        blockStartedAtMs: nowMs,
      });
      return {
        ...prev,
        currentBlockIndex: newIdx,
        elapsedSeconds: 0,
        blockStartedAt: prev.isRunning ? new Date() : null,
      };
    });
  }, [canControl, writeState]);

  const reset = useCallback(() => {
    if (!canControl) return;
    setFlow({
      entityId,
      meetingId,
      currentBlockIndex: 0,
      blockStartedAt: null,
      elapsedSeconds: 0,
      isRunning: false,
    });
    writeState({
      currentBlockIndex: 0,
      isRunning: false,
      blockStartedAtMs: null,
    });
  }, [canControl, entityId, meetingId, writeState]);

  const resetCurrentBlock = useCallback(() => {
    if (!canControl) return;
    const nowMs = flow.isRunning ? Date.now() : null;
    setFlow((prev) => ({
      ...prev,
      elapsedSeconds: 0,
      blockStartedAt: prev.isRunning ? new Date() : null,
    }));
    writeState({
      blockStartedAtMs: nowMs,
    });
  }, [canControl, flow.isRunning, writeState]);

  return {
    flow,
    currentBlock,
    elapsedSeconds: flow.elapsedSeconds,
    remainingSeconds,
    progressPercent,
    totalBlocks: sortedBlocks.length,
    canGoNext: flow.currentBlockIndex < sortedBlocks.length - 1,
    canGoPrev: flow.currentBlockIndex > 0,
    isLastBlock: flow.currentBlockIndex === sortedBlocks.length - 1,
    start,
    pause,
    nextBlock,
    prevBlock,
    reset,
    resetCurrentBlock,
  };
}
